/**
 * The page a text's share link opens, `/t/{token}` (handoff §17.2, artboard 20; ADR-115
 * Decision 10, Implementation note 4). No sign-in is needed to read it or to practise.
 *
 * - A signed-in reader who owns the text is sent to it; one who already saved a copy is
 *   sent to the copy, with a banner for that visit (the read's `viewer`).
 * - Otherwise: the title, «{n} пропусков · {w} слов», the note that nothing is kept
 *   unless the text is saved, «Сохранить в мои тексты», the text with its blanks, and
 *   «Тренировать», which runs the owner's player in viewer mode (`useSharedPractice`).
 * - «Сохранить в мои тексты» opens the save sheet: «Без колоды» or one of the reader's
 *   decks. A reader who is not signed in signs in first and comes back here with the
 *   sheet open (`?save=1`). After saving, the reader lands on their copy.
 * - A dead link, whether it never existed, was turned off or was replaced, is one page.
 *
 * The page asks search engines not to index it while it is open: the route is public
 * and the SPA has one HTML document, so a robots meta tag is the means the client has.
 *
 * It sits outside the app's Layout (a reader may have no account), in a shell of its own
 * that keeps Layout's single scrolling surface and the desktop screen-header bar, so the
 * desktop player sizes itself exactly as it does in the app.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Link2Off } from 'lucide-react'
import { useLocale } from '../i18n/context'
import { useIsDesktop } from '../hooks/useIsDesktop'
import { isDeadLink, useSaveSharedText, useSharedText } from '../hooks/useTextShare'
import { useSharedPractice } from '../hooks/useSharedPractice'
import { useAuthStore, selectIsAuthenticated, selectUser } from '../stores/authStore'
import { isDeckOwner, useDecks } from '../hooks/useDecks'
import { APP_SCROLL_ID } from '../utils/appScroll'
import { libraryHref } from '../utils/libraryAddress'
import { writeMineSegment } from '../stores/mineSegment'
import { CONTROL_SURFACE, suppressTouchContextMenu } from '../utils/controlSurface'
import type { PracticeGap, SharedText } from '../types'
import ScreenHeaderBar from '../components/ScreenHeaderBar'
import { DECK_TOP_STRIP_CLEARANCE } from '../components/DeckTopStrip'
import DeckPickerSheet from '../components/texts/DeckPickerSheet'
import PlayerText from '../components/texts/player/PlayerText'
import SheetFrame, { SheetActions } from '../components/texts/share/SheetFrame'
import { Eyebrow, PrimaryButton, SecondaryPill } from '../components/texts/ui'
import { Player } from './TextPlayerPage'

const SAVE_PARAM = 'save'

/** Keeps the page out of search indexes while it is mounted. */
function useNoIndex() {
  useEffect(() => {
    const meta = document.createElement('meta')
    meta.name = 'robots'
    meta.content = 'noindex, nofollow'
    document.head.appendChild(meta)
    return () => { meta.remove() }
  }, [])
}

export default function SharedTextPage() {
  const { token = '' } = useParams<{ token: string }>()
  const { tr } = useLocale()
  useNoIndex()
  const { data, isLoading, isError, error, refetch } = useSharedText(token)

  if (isLoading) return <Shell><div aria-busy="true" style={{ minHeight: 240 }} /></Shell>
  if (isDeadLink(error)) return <Shell><DeadLink /></Shell>
  if (isError || !data) {
    return (
      <Shell>
        <div role="alert" style={{ textAlign: 'center', padding: '64px 16px', fontFamily: 'var(--font-ui)' }}>
          <p style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{tr.textShare.loadFailed}</p>
          <SecondaryPill onClick={() => refetch()}>{tr.textShare.retry}</SecondaryPill>
        </div>
      </Shell>
    )
  }
  // The owner reads their own text; a reader with a copy reads the copy (§17.2).
  if (data.viewer?.own_text_id) return <Navigate to={`/texts/${data.viewer.own_text_id}`} replace />
  if (data.viewer?.copy_text_id) {
    return <Navigate to={`/texts/${data.viewer.copy_text_id}`} replace state={{ sharedCopy: 'existing' }} />
  }
  return <Shell><SharedTextView token={token} shared={data} /></Shell>
}

/**
 * Layout's frame without the app's navigation: the desktop screen-header bar over the one
 * scrolling surface (`#app-scroll`, ADR-067 Decision 3), with Layout's padding.
 */
function Shell({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh', overflow: 'hidden', background: 'var(--bg-page)' }}>
      <ScreenHeaderBar />
      <main
        id={APP_SCROLL_ID}
        className="max-md:!px-4 max-md:!pt-0"
        style={{ flex: 1, overflowY: 'auto', backgroundColor: 'var(--bg-page)', padding: 'var(--space-10)' }}
      >
        <div style={{ maxWidth: 'var(--page-max-width-wide)', margin: '0 auto' }}>{children}</div>
      </main>
    </div>
  )
}

function SharedTextView({ token, shared }: { token: string; shared: SharedText }) {
  const { tr } = useLocale()
  const t = tr.textShare
  const navigate = useNavigate()
  const desktop = useIsDesktop()
  const signedIn = useAuthStore(selectIsAuthenticated)
  const [params, setParams] = useSearchParams()
  const { view, actions } = useSharedPractice(token, shared)

  // Back from signing in with the sheet asked for: open it once, and take the flag out of
  // the address so a reload does not open it again.
  const [saving, setSaving] = useState(() => signedIn && params.get(SAVE_PARAM) === '1')
  useEffect(() => {
    if (params.has(SAVE_PARAM)) setParams((p) => { p.delete(SAVE_PARAM); return p }, { replace: true })
  }, [params, setParams])

  const askSave = () => {
    if (signedIn) { setSaving(true); return }
    const back = `/t/${token}?${SAVE_PARAM}=1`
    navigate(`/login?returnTo=${encodeURIComponent(back)}`)
  }

  const home = () => navigate(signedIn ? '/home' : '/')
  const meta = `${shared.gap_count > 0 ? tr.textList.gaps(shared.gap_count) : tr.textList.noGaps} · ${tr.deckTexts.words(shared.word_count)}`
  const readingHeader = useMemo(
    () => ({ back: { label: t.home, onClick: home }, title: shared.title, subtitle: meta, chip: t.eyebrow }),
    // `home` changes only with the sign-in state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t.home, t.eyebrow, shared.title, meta, signedIn],
  )

  const saveSheet = saving && (
    <SaveFlow token={token} gapCount={shared.gap_count} onClose={() => setSaving(false)} />
  )

  // A text with no gaps has nothing to practise: the page is the text and «Сохранить».
  if (shared.gap_count === 0) {
    return (
      <>
        <Reading shared={shared} meta={meta} desktop={desktop} onHome={home} onSave={askSave} />
        {saveSheet}
      </>
    )
  }

  return (
    <>
      <Player
        textId=""
        view={view}
        refetch={async () => undefined}
        actions={actions}
        viewer={{
          reading: (start) => <Reading shared={shared} meta={meta} desktop={desktop} onHome={home} onSave={askSave} onPractise={start} gaps={view.gaps} />,
          readingHeader,
          onSave: askSave,
        }}
      />
      {saveSheet}
    </>
  )
}

/** §17.2 «Opened from a link, not saved». */
function Reading({ shared, meta, desktop, onHome, onSave, onPractise, gaps }: {
  shared: SharedText
  meta: string
  desktop: boolean
  onHome: () => void
  onSave: () => void
  onPractise?: () => void
  gaps?: PracticeGap[]
}) {
  const { tr } = useLocale()
  const t = tr.textShare
  const gapMap = useMemo(() => new Map((gaps ?? []).map((g) => [g.id, g])), [gaps])
  // A text with no gaps has no gap objects to draw: its segments are text only.
  const body = (
    <div data-testid="shared-body" style={{ padding: desktop ? '28px 36px' : '16px 18px', borderRadius: 16, background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
      <PlayerText segments={shared.segments} gaps={gapMap} mode="reading" fontSize={desktop ? 18 : 16} lineHeight={desktop ? 36 : 30} activeSize={{ minWidth: 44, height: 28 }} />
    </div>
  )
  const note = (
    <p data-testid="shared-note" style={{ margin: 0, padding: '12px 14px', borderRadius: 16, background: 'var(--bg-secondary)', fontSize: 13, lineHeight: 1.45, color: 'var(--text-secondary)' }}>
      {t.note}
    </p>
  )
  const save = (
    <SecondaryPill full height={44} onClick={onSave} data-testid="shared-save">{t.saveCta}</SecondaryPill>
  )

  if (desktop) {
    return (
      <div data-testid="shared-text" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: 24, alignItems: 'start', fontFamily: 'var(--font-ui)' }}>
        {body}
        <div className={CONTROL_SURFACE} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {note}
          {onPractise && <PrimaryButton full height={48} onClick={onPractise} data-testid="shared-practise">{tr.textPlayer.train}</PrimaryButton>}
          {save}
        </div>
      </div>
    )
  }

  return (
    <div data-testid="shared-text" style={{ fontFamily: 'var(--font-ui)' }}>
      <div aria-hidden style={{ height: DECK_TOP_STRIP_CLEARANCE }} />
      <div
        className={CONTROL_SURFACE}
        onContextMenu={suppressTouchContextMenu}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 25, height: DECK_TOP_STRIP_CLEARANCE,
          paddingTop: 'env(safe-area-inset-top, 0px)', display: 'flex', alignItems: 'center', gap: 4,
          background: 'var(--bg-chrome)', borderBottom: '1px solid var(--border-chrome)',
        }}
      >
        <button
          type="button"
          onClick={onHome}
          aria-label={t.home}
          data-testid="shared-home"
          style={{ flexShrink: 0, width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-primary)', padding: 0 }}
        >
          <ChevronLeft size={24} />
        </button>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{t.home}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 0 24px' }}>
        <Eyebrow>{t.eyebrow}</Eyebrow>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.25, color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>
          {shared.title}
        </h1>
        <p data-testid="shared-meta" style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>{meta}</p>
        {note}
        <div className={CONTROL_SURFACE}>{save}</div>
        {body}
      </div>
      {onPractise && (
        <>
          <div aria-hidden style={{ height: 84 }} />
          <div
            className={CONTROL_SURFACE}
            style={{
              position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 25, padding: '12px 16px calc(12px + env(safe-area-inset-bottom, 0px))',
              background: 'var(--bg-chrome)', borderTop: '1px solid var(--border-chrome)',
            }}
          >
            <PrimaryButton full height={52} onClick={onPractise} data-testid="shared-practise">{tr.textPlayer.train}</PrimaryButton>
          </div>
        </>
      )}
    </div>
  )
}

/**
 * The save sheet (§17.2): «Без колоды» by default, «Новая колода» (a deck named after the
 * text, made with the copy, as in creation step 3) or «В колоду» through the deck picker.
 * The picker replaces the sheet while it is open and hands its choice back.
 */
function SaveFlow({ token, gapCount, onClose }: { token: string; gapCount: number; onClose: () => void }) {
  const { tr } = useLocale()
  const t = tr.textShare
  const navigate = useNavigate()
  const desktop = useIsDesktop()
  const save = useSaveSharedText(token)
  const user = useAuthStore(selectUser)
  // «В колоду» needs a deck of the reader's own: with none, the row says so and is off,
  // and the picker never opens empty.
  const decks = useDecks(1, 100)
  const noDecks = !decks.isLoading && !(decks.data?.items ?? []).some((d) => isDeckOwner(d, user?.id))
  const [deck, setDeck] = useState<{ id: string; title: string } | null>(null)
  const [dest, setDest] = useState<'none' | 'new' | 'existing'>('none')
  const [picking, setPicking] = useState(false)
  const [failed, setFailed] = useState(false)

  if (picking) {
    return (
      <DeckPickerSheet
        open
        onClose={() => setPicking(false)}
        onPick={(d) => { setDeck(d); setDest('existing'); setPicking(false) }}
      />
    )
  }

  const confirm = () => {
    setFailed(false)
    save.mutate({ deckId: dest === 'existing' && deck ? deck.id : null, newDeck: dest === 'new' }, {
      onSuccess: (r) => navigate(`/texts/${r.text_id}`, { replace: true, state: { sharedCopy: r.created ? 'saved' : 'existing' } }),
      onError: () => setFailed(true),
    })
  }
  const option = (on: boolean, title: string, sub: string, onSelect: () => void, testId: string, chevron = false, disabled = false) => (
    <button
      type="button"
      role="radio"
      aria-checked={on}
      aria-disabled={disabled || undefined}
      disabled={disabled}
      onClick={onSelect}
      data-testid={testId}
      style={{
        width: '100%', minHeight: 60, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
        background: 'none', border: 'none', cursor: disabled ? 'default' : 'pointer', fontFamily: 'var(--font-ui)',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <span aria-hidden style={{
        width: 20, height: 20, flexShrink: 0, borderRadius: '50%', boxSizing: 'border-box',
        border: on ? '6px solid var(--accent)' : '2px solid var(--border)', background: 'var(--bg-surface)',
      }} />
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{title}</span>
        <span style={{ fontSize: 13, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</span>
      </span>
      {chevron && !disabled && <ChevronRight size={18} aria-hidden style={{ flexShrink: 0, color: 'var(--text-tertiary)' }} />}
    </button>
  )
  // «В колоду» with no deck chosen yet opens the picker; with one, a tap reselects it and the
  // chevron's row opens the picker again.
  const chooseDeck = () => { if (deck && dest !== 'existing') setDest('existing'); else setPicking(true) }

  return (
    <SheetFrame title={t.saveTitle} onClose={onClose} closeLabel={t.close} testId="save-sheet">
      <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: 'var(--text-secondary)' }}>{t.saveBody(gapCount)}</p>
      <div role="radiogroup" aria-label={t.saveTitle} style={{ marginTop: 14, borderRadius: 16, border: '1px solid var(--border)', overflow: 'hidden' }}>
        {option(dest === 'none', t.noDeck, t.noDeckSub, () => setDest('none'), 'save-no-deck')}
        <div style={{ height: 1, background: 'var(--border)' }} />
        {/* Always on: it needs no deck of the reader's own. */}
        {option(dest === 'new', t.newDeck, t.newDeckSub, () => setDest('new'), 'save-new-deck')}
        <div style={{ height: 1, background: 'var(--border)' }} />
        {noDecks
          ? option(false, t.toDeck, t.noDecksYet, () => {}, 'save-to-deck', true, true)
          : option(dest === 'existing', t.toDeck, deck?.title ?? t.toDeckSub, chooseDeck, 'save-to-deck', true)}
      </div>
      <p style={{ margin: '10px 0 0', fontSize: 12, lineHeight: 1.45, color: 'var(--text-tertiary)' }}>{t.saveCaption}</p>
      {failed && <p role="alert" data-testid="save-failed" style={{ margin: '12px 0 0', fontSize: 13, color: 'var(--danger)' }}>{t.failed}</p>}
      <SheetActions>
        <PrimaryButton full={!desktop} height={desktop ? 40 : 52} onClick={confirm} disabled={save.isPending} data-testid="save-confirm">
          {save.isPending ? t.saving : t.saveConfirm}
        </PrimaryButton>
        <SecondaryPill full={!desktop} height={desktop ? 40 : 48} onClick={onClose} data-testid="save-cancel">{t.cancel}</SecondaryPill>
      </SheetActions>
    </SheetFrame>
  )
}

/** One page for every dead link: never there, turned off, or replaced by a new one. */
function DeadLink() {
  const { tr } = useLocale()
  const t = tr.textShare
  const navigate = useNavigate()
  const signedIn = useAuthStore(selectIsAuthenticated)
  return (
    <div data-testid="shared-dead" className={CONTROL_SURFACE} style={{ maxWidth: 420, margin: '0 auto', padding: '72px 0', textAlign: 'center', fontFamily: 'var(--font-ui)' }}>
      <span aria-hidden style={{
        width: 48, height: 48, margin: '0 auto 16px', borderRadius: 12, background: 'var(--bg-secondary)',
        color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Link2Off size={22} />
      </span>
      <h1 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>{t.deadTitle}</h1>
      <p style={{ margin: '0 0 20px', fontSize: 14, lineHeight: 1.5, color: 'var(--text-secondary)' }}>{t.deadBody}</p>
      {signedIn
        ? <SecondaryPill onClick={() => { writeMineSegment('texts'); navigate(libraryHref('mine')) }} data-testid="shared-dead-mine">{t.deadMine}</SecondaryPill>
        : <SecondaryPill onClick={() => navigate('/')} data-testid="shared-dead-home">{t.deadHome}</SecondaryPill>}
    </div>
  )
}
